import React, { createContext, useContext, useState, useEffect } from 'react';
import { supabase } from '@/lib/supabaseClient';
import { Session } from '@supabase/supabase-js';

export interface User {
    id: string;
    full_name: string;
    name?: string;
    email: string;
    phone?: string;
    role: 'admin' | 'student';
    status?: 'active' | 'suspended';
    created_at: string;
}

export interface AuthContextType {
    user: User | null;
    session: Session | null;
    isLoading: boolean;
    isAdmin: boolean;
    login: (email: string, password: string) => Promise<User>;
    register: (email: string, password: string, full_name: string, phone: string) => Promise<void>;
    logout: () => Promise<void>;
}

export const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [user, setUser] = useState<User | null>(null);
    const [session, setSession] = useState<Session | null>(null);
    const [isLoading, setIsLoading] = useState(true);

    const determineRole = (metadataRole?: string, profileRole?: string): 'admin' | 'student' => {
        if (metadataRole === 'admin' || profileRole === 'admin') return 'admin';
        return 'student'; // Default to student
    };

    const withTimeout = async <T,>(promise: Promise<T>, ms: number, label: string): Promise<T> => {
        let timeoutId: any;
        const timeout = new Promise<never>((_, reject) => {
            timeoutId = setTimeout(() => reject(new Error(`${label} timeout (${ms}ms)`)), ms);
        });
        return Promise.race([promise, timeout]).finally(() => clearTimeout(timeoutId));
    };

    const fetchProfile = async (userId: string, attempt = 0): Promise<any> => {
        try {
            const { data, error } = await withTimeout(
                supabase
                    .from('profiles')
                    .select('id, email, full_name, role, status, phone, created_at')
                    .eq('id', userId)
                    .maybeSingle() as any,
                1500,
                "Profile Fetch"
            ) as any;

            if (error) {
                if (attempt < 1) {
                    if (import.meta.env.DEV) console.warn(`Profile sync failed (Attempt ${attempt + 1}). Retrying...`, error);
                    return fetchProfile(userId, attempt + 1);
                }
                console.error('Database Sync Error:', error);
                return null;
            }
            return data;
        } catch (err: any) {
            if (attempt < 1 && err.message?.includes('timeout')) {
                if (import.meta.env.DEV) console.warn(`Profile sync timeout (Attempt ${attempt + 1}). Retrying...`);
                return fetchProfile(userId, attempt + 1);
            }
            if (import.meta.env.DEV) console.error('Profile fetch failed or timed out:', err);
            return null;
        }
    };

    const syncUser = async (currentSession: Session | null) => {
        if (!currentSession?.user) {
            setUser(null);
            return;
        }

        const userId = currentSession.user.id;
        const email = (currentSession.user.email || '').toLowerCase();
        const metadataRole = currentSession.user.app_metadata?.role;

        // Background Sync with Database
        let profile = await fetchProfile(userId);

        // Auto-provision student profile if completely missing
        if (!profile) {
            try {
                const { data: newProfile, error: insertError } = await withTimeout(
                    supabase
                        .from('profiles')
                        .insert([{
                            id: userId,
                            email: email,
                            full_name: currentSession.user.user_metadata?.full_name || 'User',
                            role: 'student',
                            status: 'active'
                        }])
                        .select()
                        .maybeSingle() as any,
                    1500,
                    "Profile Create"
                ) as any;

                if (!insertError && newProfile) {
                    profile = newProfile;
                }
            } catch (e) {
                if (import.meta.env.DEV) console.warn("Auto-provisioning deferred or failed:", e);
            }
        }

        const finalRole = determineRole(metadataRole, profile?.role);

        setUser({
            id: userId,
            full_name: profile?.full_name || currentSession.user.user_metadata?.full_name || 'User',
            email,
            phone: profile?.phone,
            role: finalRole,
            status: profile?.status || 'active',
            created_at: profile?.created_at || currentSession.user.created_at
        });
    };

    const initialize = async () => {
        const startTime = Date.now();
        if (import.meta.env.DEV) console.log("--- AUTH BOOT: INITIATING ---");

        try {
            const { data: { session: initialSession }, error: sessionError } = await supabase.auth.getSession();

            if (sessionError) {
                if (import.meta.env.DEV) console.error("--- AUTH BOOT: SESSION ERROR ---", sessionError);
            }

            setSession(initialSession);

            if (initialSession) {
                // Initialize user with basic data first to avoid blocking UI
                const basicUser: User = {
                    id: initialSession.user.id,
                    full_name: initialSession.user.user_metadata?.full_name || 'User',
                    email: initialSession.user.email || '',
                    role: determineRole(initialSession.user.app_metadata?.role),
                    status: 'active',
                    created_at: initialSession.user.created_at
                };
                setUser(basicUser);

                // Sync in background - No await here
                syncUser(initialSession).catch(err => {
                    if (import.meta.env.DEV) console.warn("--- AUTH BOOT: SYNC DEFERRED ---", err);
                }).finally(() => {
                    setIsLoading(false);
                    if (import.meta.env.DEV) console.log(`--- AUTH BOOT: SYNC DONE (${Date.now() - startTime}ms) ---`);
                });
            } else {
                setIsLoading(false);
            }
        } catch (error) {
            if (import.meta.env.DEV) console.error('--- AUTH BOOT: CRITICAL FAILURE ---', error);
            setIsLoading(false);
        }
    };

    useEffect(() => {
        initialize();

        const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, currentSession) => {
            setSession(currentSession);
            if (currentSession) {
                syncUser(currentSession);
            } else {
                setUser(null);
            }
            setIsLoading(false);
        });

        return () => subscription.unsubscribe();
    }, []);

    const login = async (email: string, password: string) => {
        setIsLoading(true);
        if (import.meta.env.DEV) console.log(`--- AUTH ATTEMPT: ${email} ---`);

        try {
            const { data, error } = await withTimeout(
                supabase.auth.signInWithPassword({
                    email: email.trim(),
                    password: password.trim()
                }),
                10000,
                "Authentication"
            );

            if (error) throw error;
            if (!data?.session) throw new Error("No session established");

            const currentSession = data.session;
            setSession(currentSession);

            // Construct basic user for IMMEDIATE navigation
            const authenticatedUser: User = {
                id: currentSession.user.id,
                full_name: currentSession.user.user_metadata?.full_name || 'User',
                email: currentSession.user.email || email,
                role: determineRole(currentSession.user.app_metadata?.role),
                status: 'active',
                created_at: currentSession.user.created_at
            };

            setUser(authenticatedUser);

            // BACKGROUND SYNC: Fire and forget
            syncUser(currentSession).catch(err => {
                if (import.meta.env.DEV) console.warn("Background Sync Failure (Non-Critical):", err);
            });

            return authenticatedUser;
        } finally {
            setIsLoading(false);
        }
    };

    const register = async (email: string, password: string, full_name: string, phone: string) => {
        const { error } = await supabase.auth.signUp({
            email,
            password,
            options: { data: { full_name, phone } }
        });
        if (error) throw error;
    };

    const logout = async () => {
        await supabase.auth.signOut();
        setUser(null);
        setSession(null);
    };

    const isAdmin = user?.role === 'admin';

    return (
        <AuthContext.Provider value={{ user, session, isLoading, isAdmin, login, register, logout }}>
            {children}
        </AuthContext.Provider>
    );
};
